import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Button } from 'antd'
import { useLocation } from 'react-router-dom'
import { FiltersFastButtonsRow, FiltersFastButtonsWrapper } from './styles'
import { useMediaQuery } from 'react-responsive'

interface IFilter {
  name: string
  code: string
  value: string
  style?: React.CSSProperties
}

interface IButton extends Omit<IFilter, 'style'> {
  isActive: boolean
}

interface IFastFiltersPersistence {
  storageKey: string
  managedCodes: string[]
}

interface IFilterButtons {
  filters: IFilter[]
  onChange: (params: Record<string, string | string[]>, replace?: boolean) => void
  persistence?: IFastFiltersPersistence
  onPersistenceReady?: () => void
}

interface IPersistedFastFilters {
  version: number
  params: Record<string, string>
}

const STORAGE_PREFIX = 'fastFilters:'
const STORAGE_VERSION = 1

const getStorageItemKey = (storageKey: string) => `${STORAGE_PREFIX}${storageKey}`

export const clearPersistedFastFilters = (storageKey: string) => {
  try {
    localStorage.removeItem(getStorageItemKey(storageKey))
  } catch {}
}

const getFilterValues = (value: string) =>
  value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)

const getGroupedActiveFilters = (buttons: IButton[]) => {
  const groupedFilters: Record<string, string[]> = {}

  buttons.forEach(button => {
    if (!button.isActive || button.value === 'all') {
      return
    }
    if (!groupedFilters[button.code]) {
      groupedFilters[button.code] = []
    }
    groupedFilters[button.code].push(...getFilterValues(button.value))
  })

  return Object.fromEntries(
    Object.entries(groupedFilters).flatMap(([code, values]) => {
      const uniqueValues = [...new Set(values)]
      return uniqueValues.length > 0 ? [[code, uniqueValues.join(',')]] : []
    }),
  ) as Record<string, string>
}

const readPersistedParams = (
  persistence: IFastFiltersPersistence,
  filters: IFilter[],
): Record<string, string> | null => {
  const storageItemKey = getStorageItemKey(persistence.storageKey)

  try {
    const rawValue = localStorage.getItem(storageItemKey)
    if (!rawValue) {
      return null
    }

    const parsed = JSON.parse(rawValue) as IPersistedFastFilters
    if (
      parsed?.version !== STORAGE_VERSION ||
      !parsed.params ||
      typeof parsed.params !== 'object' ||
      Array.isArray(parsed.params)
    ) {
      localStorage.removeItem(storageItemKey)
      return null
    }

    const allowedValuesByCode = persistence.managedCodes.reduce((acc, code) => {
      acc[code] = new Set(
        filters
          .filter(filter => filter.code === code && filter.value !== 'all')
          .flatMap(filter => getFilterValues(filter.value)),
      )
      return acc
    }, {} as Record<string, Set<string>>)

    const restoredParams: Record<string, string> = {}

    for (const code of persistence.managedCodes) {
      const storedValue = parsed.params[code]
      if (typeof storedValue !== 'string' || !storedValue.trim()) {
        continue
      }

      const values = [...new Set(getFilterValues(storedValue))]
      const allowedValues = allowedValuesByCode[code]

      if (
        values.length === 0 ||
        !allowedValues ||
        values.some(value => !allowedValues.has(value))
      ) {
        localStorage.removeItem(storageItemKey)
        return null
      }

      restoredParams[code] = values.join(',')
    }

    if (Object.keys(restoredParams).length === 0) {
      localStorage.removeItem(storageItemKey)
      return null
    }

    return restoredParams
  } catch {
    localStorage.removeItem(storageItemKey)
    return null
  }
}

const savePersistedParams = (
  persistence: IFastFiltersPersistence,
  params: Record<string, string>,
) => {
  const storageItemKey = getStorageItemKey(persistence.storageKey)
  const managedParams = Object.fromEntries(
    Object.entries(params).filter(
      ([code, value]) => persistence.managedCodes.includes(code) && Boolean(value),
    ),
  )

  try {
    if (Object.keys(managedParams).length === 0) {
      localStorage.removeItem(storageItemKey)
      return
    }

    localStorage.setItem(
      storageItemKey,
      JSON.stringify({
        version: STORAGE_VERSION,
        params: managedParams,
      } as IPersistedFastFilters),
    )
  } catch {}
}

export const FiltersFastButtons = memo(function FiltersFastButtons({
  filters,
  onChange,
  persistence,
  onPersistenceReady,
}: IFilterButtons) {
  const location = useLocation()
  const [buttons, setButtons] = useState<IButton[]>([])
  const [isUpd, setIsUpd] = useState(false)
  const isRestoreHandledRef = useRef(false)
  const isRestorePendingRef = useRef(false)
  const isPersistenceReadyNotifiedRef = useRef(false)
  const isMobile = useMediaQuery({ maxWidth: 768 })

  const notifyPersistenceReady = useCallback(() => {
    if (!onPersistenceReady || isPersistenceReadyNotifiedRef.current) {
      return
    }

    isPersistenceReadyNotifiedRef.current = true
    onPersistenceReady()
  }, [onPersistenceReady])

  useEffect(() => {
    if (!filters.length) {
      return
    }

    const urlSearchParams = new URLSearchParams(location.search)
    const buttons = filters.map(filter => {
      const currentFilters = urlSearchParams.get(filter.code)?.split(',') || []
      const buttonValues = filter.value.split(',')

      const isActive =
        buttonValues.length > 0 && buttonValues.every(val => currentFilters.includes(val))

      return {
        name: filter.name,
        code: filter.code,
        value: filter.value,
        isActive,
      }
    })

    setButtons(buttons)
  }, [filters, location.search])

  useEffect(() => {
    isRestoreHandledRef.current = false
    isRestorePendingRef.current = false
    isPersistenceReadyNotifiedRef.current = false
  }, [persistence?.storageKey])

  useEffect(() => {
    if (!filters.length) {
      return
    }

    if (!persistence) {
      notifyPersistenceReady()
      return
    }

    if (isRestoreHandledRef.current) {
      return
    }

    isRestoreHandledRef.current = true
    const managedCodes = persistence.managedCodes.filter(Boolean)

    if (managedCodes.length === 0) {
      notifyPersistenceReady()
      return
    }

    const urlSearchParams = new URLSearchParams(location.search)
    const hasManagedParamsInUrl = managedCodes.some(code => {
      const value = urlSearchParams.get(code)
      return Boolean(value && value.trim())
    })

    if (hasManagedParamsInUrl) {
      notifyPersistenceReady()
      return
    }

    const restoredParams = readPersistedParams(persistence, filters)
    if (!restoredParams) {
      notifyPersistenceReady()
      return
    }

    const nextParams: Record<string, string> = {}
    urlSearchParams.forEach((value, key) => {
      nextParams[key] = value
    })

    isRestorePendingRef.current = true
    onChange(
      {
        ...nextParams,
        ...restoredParams,
      },
      true,
    )
  }, [filters, location.search, notifyPersistenceReady, onChange, onPersistenceReady, persistence])

  useEffect(() => {
    if (!persistence || !isRestorePendingRef.current) {
      return
    }

    const managedCodes = persistence.managedCodes.filter(Boolean)
    const urlSearchParams = new URLSearchParams(location.search)
    const hasManagedParamsInUrl = managedCodes.some(code => {
      const value = urlSearchParams.get(code)
      return Boolean(value && value.trim())
    })

    if (!hasManagedParamsInUrl) {
      return
    }

    isRestorePendingRef.current = false
    notifyPersistenceReady()
  }, [location.search, notifyPersistenceReady, onPersistenceReady, persistence])

  useEffect(() => {
    if (!isUpd) return

    const urlSearchParams = new URLSearchParams(location.search)
    const newParams: Record<string, string> = {}

    urlSearchParams.forEach((value, key) => {
      if (!filters.some(filter => filter.code === key)) {
        newParams[key] = value
      }
    })

    const groupedFilters = getGroupedActiveFilters(buttons)
    Object.assign(newParams, groupedFilters)

    if (persistence) {
      savePersistedParams(persistence, groupedFilters)
    }

    onChange(newParams, true)
    setIsUpd(false)
  }, [buttons, filters, isUpd, location.search, onChange, persistence])

  const onClickButton = (value: string, code: string) => () => {
    setButtons(prevState => {
      const newState = prevState.map(btn => {
        if (btn.value === value && btn.code === code) {
          return { ...btn, isActive: !btn.isActive }
        }
        return btn
      })
      return newState
    })
    setIsUpd(true)
  }

  return (
    <FiltersFastButtonsWrapper>
      <FiltersFastButtonsRow>
        {buttons.map(button => {
          const filter = filters.find(f => f.code === button.code && f.value === button.value)
          const buttonStyle = filter?.style || {}

          return (
            <Button
              key={`${button.code}-${button.value}`}
              type={button.isActive ? 'primary' : 'default'}
              onClick={onClickButton(button.value, button.code)}
              size={isMobile ? 'middle' : 'small'}
              style={{
                color: button.isActive ? buttonStyle.color : 'inherit',
                backgroundColor: button.isActive ? buttonStyle.backgroundColor : '#00000005',
                border: `1px solid ${buttonStyle.backgroundColor || '#d9d9d9'}`,
                lineHeight: 'normal',
                boxShadow: 'none',
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: button.name }} />
            </Button>
          )
        })}
      </FiltersFastButtonsRow>
    </FiltersFastButtonsWrapper>
  )
})
